import type { ViewRenderKind } from '@hibro/shared';

/**
 * Whether a view should render as an `iframe` surface (见 9): a view whose render
 * kind is `iframe` with a content URL and an `IframeBridge` to the platform.
 * Mounts a sandboxed `<iframe>` directly in the DOM — the only hosting for
 * external web content, inline or detached (见 09).
 * Keyed on the view-level render kind (架构方案 §5.8), so a module that ships its
 * own renderer UI may host an iframe view too. Pure so the render-kind decision
 * stays unit-testable.
 */
export function isIframeSurface(
  kind: ViewRenderKind | undefined,
  url: string | undefined,
  hasBridge: boolean,
): boolean {
  return kind === 'iframe' && hasBridge && !!url;
}

/**
 * Whether a view is backed by an `external` process (架构方案 §3.1/§5.8): it has
 * no inline content, so the SurfaceHost renders a control panel that
 * launches/focuses/closes the external app instead of mounting a component.
 * Keyed on the view-level render kind. Pure so the render-kind decision stays
 * unit-testable.
 */
export function isExternalSurface(kind: ViewRenderKind | undefined): boolean {
  return kind === 'external';
}

/**
 * Whether a view has the `remote` render kind (架构方案 §3 —
 * architecture-reserved, not implemented this phase). The SurfaceHost shows an
 * explicit "not available" message instead of letting the view fall through to
 * the inline-dom host, where it would spin on "Loading view…" forever
 * (见 08). Pure so the guard stays unit-testable.
 */
export function isRemoteSurface(kind: ViewRenderKind | undefined): boolean {
  return kind === 'remote';
}
